/**
 * Hook for consuming live streaming transcript.
 * Primary source: WebSocket (same connection as audio — guaranteed same pod).
 * Fallback: SSE /api/events (disabled when WS is attached to prevent duplicates).
 */

import type {
  IFacilitatorSuggestion,
  IProgressiveSummary,
  ISpeakerHypothesisSnapshot,
  IStreamingSegment,
  TCoachCategory,
  TStreamControlMessage,
} from "@lexicanon/shared"
import { SSE_EVENTS } from "@lexicanon/shared"
import { useCallback, useEffect, useRef, useState } from "react"
import { rememberLastUsedLanguages } from "../lib/last-used-languages"
import { createReconnectingEventSource } from "./useSSE"

export type { IFacilitatorSuggestion, IProgressiveSummary, ISpeakerHypothesisSnapshot, IStreamingSegment }

/** Live language-switch progress as pushed on the recording WS. */
export interface ILanguageSignal {
  current: string
  candidate: string | null
  candidateSeconds: number
  thresholdSeconds: number
}

/** One-shot "this sounds like X" prompt. Null when there is nothing to answer. */
export interface ILanguageSuggestion {
  current: string
  candidate: string
}

/** The room or remote audio socket. A browser `WebSocket` satisfies this. */
interface ILiveSocket {
  readyState: number
  send: (data: string) => void
  addEventListener: (type: "message", listener: (event: MessageEvent) => void) => void
  removeEventListener: (type: "message", listener: (event: MessageEvent) => void) => void
}

interface IStreamingMessage extends Partial<ISpeakerHypothesisSnapshot> {
  type: string
  meetingId?: string
  segment?: IStreamingSegment
  text?: string
  speaker?: string
  topics?: string[]
  currentDiscussion?: string
  keyPoints?: string[]
  speakerCount?: number
  // speaker_identified (legacy)
  name?: string
  confirmed?: boolean
  // facilitator_suggestion (coach)
  category?: TCoachCategory
  question?: string
  why?: string
  timestamp?: string
  // language_signal / meeting_language_changed
  current?: string
  candidate?: string | null
  candidateSeconds?: number
  thresholdSeconds?: number
  from?: string
  to?: string
}

interface IStreamingTranscriptState {
  segments: IStreamingSegment[]
  interimText: string
  interimSpeaker: string
  progressiveSummary: IProgressiveSummary | null
  /** Number of progressive_summary events actually received from the backend.
   *  Drives the "X updates" counter on InsightsCard — a real signal, not a
   *  time-based estimate. Stays at 0 when the live-insights pipeline is silent
   *  (the symptom of the 2026-05-18 sidebar regression). */
  progressiveSummaryCount: number
  /** ISO timestamp of the most recent progressive_summary event, or null if
   *  none have arrived yet. Lets the UI surface a "last update Xs ago" hint
   *  when the stream goes quiet. */
  progressiveSummaryAt: string | null
  /** Latest live facilitator-coach suggestion ("Worth asking"), or null. */
  facilitatorSuggestion: IFacilitatorSuggestion | null
  /** Recent facilitator suggestions, newest first (capped). */
  facilitatorHistory: IFacilitatorSuggestion[]
  /** Per-label hypothesis snapshots from the backend speaker-hypothesis state machine. */
  speakerHypotheses: Map<string, ISpeakerHypothesisSnapshot>
  /** Live language-switch progress, or null when no candidate is accumulating. */
  languageSignal: ILanguageSignal | null
  /** Offer to switch the spoken language, or null when none is pending. */
  languageSuggestion: ILanguageSuggestion | null
  /** Tell the server to switch the meeting to the suggested language. */
  acceptLanguageSwitch: () => void
  /** Tell the server to keep the current language for the suggested candidate. */
  keepCurrentLanguage: () => void
  /** Committed meeting language after a live switch; null until the first flip. */
  meetingLanguage: string | null
  isStreaming: boolean
  /** True while the SSE fallback connection is down and a reconnect is pending
   *  (5xx / pod restart). Only meaningful in SSE-fallback mode (no WS attached).
   *  Additive — existing consumers can ignore it. */
  isReconnecting: boolean
  attachWs: (ws: ILiveSocket) => void
  detachWs: () => void
  attachRemoteWs: (ws: ILiveSocket) => void
  detachRemoteWs: () => void
}

export function useStreamingTranscript(meetingId: string, companyId?: string | null): IStreamingTranscriptState {
  const [segments, setSegments] = useState<IStreamingSegment[]>([])
  const [interimText, setInterimText] = useState("")
  const [interimSpeaker, setInterimSpeaker] = useState("")
  const [progressiveSummary, setProgressiveSummary] = useState<IProgressiveSummary | null>(null)
  const [progressiveSummaryCount, setProgressiveSummaryCount] = useState<number>(0)
  const [progressiveSummaryAt, setProgressiveSummaryAt] = useState<string | null>(null)
  const [facilitatorSuggestion, setFacilitatorSuggestion] = useState<IFacilitatorSuggestion | null>(null)
  const [facilitatorHistory, setFacilitatorHistory] = useState<IFacilitatorSuggestion[]>([])
  const [speakerHypotheses, setSpeakerHypotheses] = useState<Map<string, ISpeakerHypothesisSnapshot>>(new Map())
  const [languageSignal, setLanguageSignal] = useState<ILanguageSignal | null>(null)
  const [languageSuggestion, setLanguageSuggestion] = useState<ILanguageSuggestion | null>(null)
  const [meetingLanguage, setMeetingLanguage] = useState<string | null>(null)
  const languageSuggestionRef = useRef<ILanguageSuggestion | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [wsAttached, setWsAttached] = useState(false)
  // label → real name map for real-time speaker identification
  const speakerNamesRef = useRef<Map<string, string>>(new Map())
  const meetingIdRef = useRef(meetingId)
  meetingIdRef.current = meetingId
  const wsListenerRef = useRef<((event: MessageEvent) => void) | null>(null)
  const currentWsRef = useRef<ILiveSocket | null>(null)
  // Second WS slot for the hybrid "remote" (system-loopback) channel, so its
  // segments interleave into the same live transcript as the mic channel.
  const remoteWsListenerRef = useRef<((event: MessageEvent) => void) | null>(null)
  const remoteWsRef = useRef<ILiveSocket | null>(null)

  const resolveSpeaker = useCallback((label: string): string => {
    return speakerNamesRef.current.get(label) ?? label
  }, [])

  const clearLanguageSuggestion = useCallback((): void => {
    languageSuggestionRef.current = null
    setLanguageSuggestion(null)
  }, [])

  const rememberSuggestion = useCallback((next: ILanguageSuggestion): void => {
    languageSuggestionRef.current = next
    setLanguageSuggestion(next)
  }, [])

  const handleMessage = useCallback(
    (data: IStreamingMessage): void => {
      if (data.meetingId && data.meetingId !== meetingIdRef.current) return

      switch (data.type) {
        case SSE_EVENTS.STREAMING_STARTED:
          setIsStreaming(true)
          break

        case SSE_EVENTS.STREAMING_SEGMENT:
          if (data.segment) {
            const seg = { ...data.segment, speaker: resolveSpeaker(data.segment.speaker) }
            setSegments((prev) => [...prev, seg])
            setInterimText("")
            setInterimSpeaker("")
          }
          break

        case SSE_EVENTS.STREAMING_INTERIM:
          setInterimText(data.text ?? "")
          setInterimSpeaker(resolveSpeaker(data.speaker ?? ""))
          setIsStreaming(true)
          break

        case SSE_EVENTS.SPEAKER_IDENTIFIED:
          if (data.label && data.name) {
            // Append "?" suffix for tentative matches; confirmed matches use the plain name
            const displayName = data.confirmed === false ? `${data.name}?` : data.name
            speakerNamesRef.current.set(data.label, displayName)
            // Retroactively rename already-stored segments with this label (or a prior tentative label)
            setSegments((prev) =>
              prev.map((s) =>
                s.speaker === data.label || s.speaker === `${data.name}?` ? { ...s, speaker: displayName } : s,
              ),
            )
          }
          break

        case SSE_EVENTS.SPEAKER_HYPOTHESIS:
          if (data.label) {
            const snap: ISpeakerHypothesisSnapshot = {
              label: data.label,
              status: data.status ?? "unidentified",
              confirmedName: data.confirmedName ?? null,
              best: data.best ?? null,
              candidates: data.candidates ?? [],
            }
            setSpeakerHypotheses((prev) => {
              const next = new Map(prev)
              next.set(snap.label, snap)
              return next
            })
            // When a label is confirmed, retroactively rename its segments
            // so the live transcript shows the real name.
            if (snap.status === "confirmed" && snap.confirmedName) {
              const confirmed = snap.confirmedName
              speakerNamesRef.current.set(snap.label, confirmed)
              setSegments((prev) =>
                prev.map((s) =>
                  s.speaker === snap.label || s.speaker === `${confirmed}?` ? { ...s, speaker: confirmed } : s,
                ),
              )
            }
          }
          break

        case SSE_EVENTS.PROGRESSIVE_SUMMARY:
          setProgressiveSummary({
            topics: data.topics ?? [],
            currentDiscussion: data.currentDiscussion ?? "",
            keyPoints: data.keyPoints ?? [],
            speakerCount: data.speakerCount ?? 0,
          })
          setProgressiveSummaryCount((n) => n + 1)
          setProgressiveSummaryAt(new Date().toISOString())
          break

        case "facilitator_suggestion":
          if (data.question && data.category) {
            const suggestion: IFacilitatorSuggestion = {
              category: data.category,
              question: data.question,
              why: data.why ?? "",
              timestamp: data.timestamp ?? new Date().toISOString(),
            }
            setFacilitatorSuggestion(suggestion)
            setFacilitatorHistory((prev) => [suggestion, ...prev].slice(0, 6))
          }
          break

        case SSE_EVENTS.LANGUAGE_SIGNAL: {
          const candidate = data.candidate ?? null
          if (!candidate) {
            setLanguageSignal(null)
            clearLanguageSuggestion()
            break
          }
          setLanguageSignal({
            current: data.current ?? "",
            candidate,
            candidateSeconds: data.candidateSeconds ?? 0,
            thresholdSeconds: data.thresholdSeconds ?? 0,
          })
          if (languageSuggestionRef.current && languageSuggestionRef.current.candidate !== candidate) {
            clearLanguageSuggestion()
          }
          break
        }

        case SSE_EVENTS.LANGUAGE_SUGGESTION:
          if (data.current && data.candidate) {
            rememberSuggestion({ current: data.current, candidate: data.candidate })
          }
          break

        case SSE_EVENTS.MEETING_LANGUAGE_CHANGED:
          if (data.to) {
            setMeetingLanguage(data.to)
            setLanguageSignal(null)
            clearLanguageSuggestion()
          }
          break

        case SSE_EVENTS.STREAMING_STOPPED:
        case SSE_EVENTS.STREAMING_FALLBACK:
          setIsStreaming(false)
          break
      }
    },
    [clearLanguageSuggestion, rememberSuggestion, resolveSpeaker],
  )

  const attachWs = useCallback(
    (ws: ILiveSocket): void => {
      // Clean up previous listener
      if (currentWsRef.current && wsListenerRef.current) {
        currentWsRef.current.removeEventListener("message", wsListenerRef.current)
      }

      const listener = (event: MessageEvent): void => {
        if (typeof event.data !== "string") return
        try {
          const data: IStreamingMessage = JSON.parse(event.data)
          handleMessage(data)
        } catch {
          // Not a JSON message — ignore
        }
      }

      ws.addEventListener("message", listener)
      wsListenerRef.current = listener
      currentWsRef.current = ws
      setWsAttached(true)
    },
    [handleMessage],
  )

  const detachWs = useCallback((): void => {
    if (currentWsRef.current && wsListenerRef.current) {
      currentWsRef.current.removeEventListener("message", wsListenerRef.current)
    }
    currentWsRef.current = null
    wsListenerRef.current = null
    setWsAttached(false)
  }, [])

  // Hybrid "remote" channel: a second WS whose segments feed the same handler,
  // so the live transcript shows mic + system-audio interleaved (remote speakers
  // arrive namespaced `Remote::…`).
  const attachRemoteWs = useCallback(
    (ws: ILiveSocket): void => {
      if (remoteWsRef.current && remoteWsListenerRef.current) {
        remoteWsRef.current.removeEventListener("message", remoteWsListenerRef.current)
      }
      const listener = (event: MessageEvent): void => {
        if (typeof event.data !== "string") return
        try {
          const data: IStreamingMessage = JSON.parse(event.data)
          handleMessage(data)
        } catch {
          // Not a JSON message — ignore
        }
      }
      ws.addEventListener("message", listener)
      remoteWsListenerRef.current = listener
      remoteWsRef.current = ws
    },
    [handleMessage],
  )

  const detachRemoteWs = useCallback((): void => {
    if (remoteWsRef.current && remoteWsListenerRef.current) {
      remoteWsRef.current.removeEventListener("message", remoteWsListenerRef.current)
    }
    remoteWsRef.current = null
    remoteWsListenerRef.current = null
  }, [])

  // Same socket RecordingControls uses for `coach_ask`: the room audio WS
  // attached via `attachWs`. One open socket, one control frame.
  const sendControl = useCallback((payload: TStreamControlMessage): boolean => {
    const ws = currentWsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload))
      return true
    }
    return false
  }, [])

  const acceptLanguageSwitch = useCallback((): void => {
    const suggestion = languageSuggestionRef.current
    if (!suggestion) return
    const sent = sendControl({ type: "language_switch", to: suggestion.candidate })
    if (!sent) return
    rememberLastUsedLanguages({ languages: [suggestion.candidate], autoDetect: false })
  }, [sendControl])

  const keepCurrentLanguage = useCallback((): void => {
    const suggestion = languageSuggestionRef.current
    if (!suggestion) return
    const sent = sendControl({ type: "language_keep", candidate: suggestion.candidate })
    if (!sent) return
    clearLanguageSuggestion()
  }, [clearLanguageSuggestion, sendControl])

  // SSE fallback — only active when no WS is attached
  useEffect(() => {
    if (wsAttached || !companyId) {
      setIsReconnecting(false)
      return
    }

    const onMessage = (event: MessageEvent<string>): void => {
      try {
        const data: IStreamingMessage = JSON.parse(event.data)
        handleMessage(data)
      } catch {
        // ignore
      }
    }

    return createReconnectingEventSource(`/api/companies/${companyId}/events`, false, onMessage, (connected) =>
      setIsReconnecting(!connected),
    )
  }, [handleMessage, wsAttached, companyId])

  // Cleanup on unmount
  useEffect(() => {
    return (): void => {
      if (currentWsRef.current && wsListenerRef.current) {
        currentWsRef.current.removeEventListener("message", wsListenerRef.current)
      }
      currentWsRef.current = null
      wsListenerRef.current = null
    }
  }, [])

  return {
    segments,
    interimText,
    interimSpeaker,
    progressiveSummary,
    progressiveSummaryCount,
    progressiveSummaryAt,
    facilitatorSuggestion,
    facilitatorHistory,
    speakerHypotheses,
    languageSignal,
    languageSuggestion,
    acceptLanguageSwitch,
    keepCurrentLanguage,
    meetingLanguage,
    isStreaming,
    isReconnecting,
    attachWs,
    detachWs,
    attachRemoteWs,
    detachRemoteWs,
  }
}
